// src/screens/PlaybookDetails.tsx
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { ArrowLeft, BookOpen, Pencil, Loader2, PlayCircle } from "lucide-react";
import i18n from "../i18n";

type TeamMini = { team_name: string };
type PlayMini = { id: string; name: string };

type PlaybookRow = {
  id: string;
  name: string;
  description?: string | null;
  created_at: string;
  team_id: string;
  team?: TeamMini | null; // jointure normalisée en objet unique
};

type Block = {
  id: string;
  title: string;
  order_idx: number;
  items: BlockItem[];
};

type BlockItem = {
  id: string;
  block_id: string;
  play_id: string;
  order_idx: number;
  notes?: string | null;
  play?: PlayMini | null;
};

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const PlaybookDetails = () => {
  const { t, lng } = useI18nSafe();

  const { id } = useParams();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [playbook, setPlaybook] = useState<PlaybookRow | null>(null);
  const [blocks, setBlocks] = useState<Block[]>([]);

  useEffect(() => {
    (async () => {
      if (!id) return;
      setLoading(true);
      try {
        // 1) Playbook + team
        const { data: pb } = await supabase
          .from("playbooks")
          .select(`id, name, description, created_at, team_id, teams:teams ( team_name )`)
          .eq("id", id)
          .maybeSingle();

        if (!pb) {
          setPlaybook(null);
          setBlocks([]);
          return;
        }

        const normalized: PlaybookRow = {
          id: pb.id,
          name: pb.name,
          description: pb.description,
          created_at: pb.created_at,
          team_id: pb.team_id,
          team: Array.isArray(pb.teams) ? (pb.teams[0] ?? null) : (pb.teams ?? null),
        };
        setPlaybook(normalized);

        // 2) Blocs
        const { data: pbb } = await supabase
          .from("playbook_blocks")
          .select("id, title, order_idx")
          .eq("playbook_id", id)
          .order("order_idx", { ascending: true });

        const blockRows = (pbb || []).map((b) => ({
          id: b.id,
          title: b.title,
          order_idx: b.order_idx,
          items: [] as BlockItem[],
        }));

        if (blockRows.length === 0) {
          setBlocks(blockRows);
          return;
        }

        const blockIds = blockRows.map((b) => b.id);

        // 3) Items (avec join plays)
        const { data: items } = await supabase
          .from("playbook_items")
          .select("id, block_id, play_id, order_idx, notes, plays(id, name)")
          .in("block_id", blockIds)
          .order("order_idx", { ascending: true });

        const byBlock = new Map<string, BlockItem[]>();
        (items || []).forEach((it: any) => {
          const arr = byBlock.get(it.block_id) || [];
          arr.push({
            id: it.id,
            block_id: it.block_id,
            play_id: it.play_id,
            order_idx: it.order_idx,
            notes: it.notes ?? null,
            play: it.plays ? { id: it.plays.id, name: it.plays.name } : null,
          });
          byBlock.set(it.block_id, arr);
        });

        const withItems: Block[] = blockRows.map((b) => ({
          ...b,
          items: (byBlock.get(b.id) || []).sort((a, z) => a.order_idx - z.order_idx),
        }));

        setBlocks(withItems.sort((a, z) => a.order_idx - z.order_idx));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const totals = useMemo(() => {
    const nbBlocks = blocks.length;
    const nbItems = blocks.reduce((acc, b) => acc + b.items.length, 0);
    return { nbBlocks, nbItems };
  }, [blocks]);

  const fmtDate = (iso?: string) => {
    if (!iso) return "—";
    const locale = lng === "fr" ? "fr-FR" : "en-US";
    return new Date(iso).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2">
        <Loader2 className="animate-spin" /> {t("common:loading")}
      </div>
    );
  }

  if (!playbook) {
    return (
      <div className="max-w-5xl mx-auto p-4">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border dark:border-zinc-800 mb-4"
        >
          <ArrowLeft size={18} /> {t("common:back")}
        </button>
        <div className="rounded-2xl border dark:border-zinc-800 p-6">
          {t("playbookdetails:not_found")}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
        <div className="flex-1">
          <div className="text-sm opacity-70 flex items-center gap-2">
            <BookOpen size={18} /> {t("playbookdetails:header.subtitle")}
          </div>
          <h1 className="text-2xl font-semibold mt-1">{playbook.name}</h1>
          <div className="text-sm opacity-70 mt-1">
            {t("playbookdetails:header.team")}: {playbook.team?.team_name ?? "—"} · {t("playbookdetails:header.created_on", { date: fmtDate(playbook.created_at) })}
          </div>
          {playbook.description && (
            <p className="mt-3 text-sm opacity-90 whitespace-pre-wrap">{playbook.description}</p>
          )}
          <div className="mt-3 text-xs opacity-60">
            {t("playbookdetails:counts.blocks", { count: totals.nbBlocks })} · {t("playbookdetails:counts.plays", { count: totals.nbItems })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/playbooks"
            className="px-3 py-2 rounded-xl border dark:border-zinc-800 inline-flex items-center gap-2"
          >
            <ArrowLeft size={16} /> {t("playbookdetails:actions.back_to_list")}
          </Link>
          <Link
            to={`/playbooks/${playbook.id}`}
            className="px-3 py-2 rounded-xl bg-black text-white dark:bg-white dark:text-black inline-flex items-center gap-2"
          >
            <Pencil size={16} /> {t("playbookdetails:actions.edit")}
          </Link>
        </div>
      </div>

      {/* Blocs */}
      {blocks.length === 0 ? (
        <div className="rounded-2xl border dark:border-zinc-800 p-8 text-center">
          {t("playbookdetails:empty.no_blocks")}
        </div>
      ) : (
        <div className="space-y-4">
          {blocks.map((b) => (
            <section key={b.id} className="rounded-2xl border dark:border-zinc-800">
              <div className="px-4 py-3 border-b dark:border-zinc-800 flex items-center justify-between">
                <h2 className="text-lg font-semibold">{b.title}</h2>
                <div className="text-xs opacity-60">
                  {t("playbookdetails:counts.plays", { count: b.items.length })}
                </div>
              </div>

              {b.items.length === 0 ? (
                <div className="p-4 text-sm opacity-70">{t("playbookdetails:empty.no_plays_in_block")}</div>
              ) : (
                <ul className="p-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {b.items.map((it) => (
                    <li key={it.id} className="rounded-xl border dark:border-zinc-800 p-3 bg-white dark:bg-zinc-950">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium">
                          {it.play?.name ?? t("playbookdetails:card.play_fallback")}
                        </div>
                        {/* Lien optionnel vers la page détails du play */}
                        {it.play?.id && (
                          <Link
                            to={`/play/${it.play.id}`}
                            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-lg border dark:border-zinc-800"
                            title={t("playbookdetails:card.view_play_title")}
                          >
                            <PlayCircle size={14} /> {t("playbookdetails:card.view")}
                          </Link>
                        )}
                      </div>
                      {it.notes && (
                        <div className="mt-2 text-sm opacity-80 whitespace-pre-wrap">
                          {it.notes}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
};
